// Persistent POS-line → product mappings.
//
// When a user manually maps an unknown POS line to a product (via the
// "Map to product" dropdown in the upload preview), we remember the
// mapping in localStorage so future uploads of the same line skip
// straight to that product instead of going through fuzzyMatch again.
//
// Mapping key is a normalised form of the POS line's raw name so the
// same product matches across casing / whitespace / punctuation
// variations. Mapping value is the canonical product code.
//
// Storage key:  desres_product_mappings_{dept}
// Shape:        { [normalisedRawName]: productCode }

const KEY_PREFIX = 'desres_product_mappings_'

/** Lowercase, alpha-numeric-only, single-spaced. The same input
 *  always produces the same key. */
export function normaliseMappingKey(rawName) {
  return String(rawName || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function lsKey(dept) { return `${KEY_PREFIX}${dept}` }

/** Return the full mapping object for a department, or {} if none. */
export function getProductMappings(dept) {
  if (!dept) return {}
  try {
    return JSON.parse(localStorage.getItem(lsKey(dept)) || '{}')
  } catch {
    return {}
  }
}

/** Look up the product code mapped to a raw POS name. Returns null
 *  if no mapping exists. */
export function lookupProductMapping(dept, rawName) {
  const key = normaliseMappingKey(rawName)
  if (!key) return null
  const mappings = getProductMappings(dept)
  return mappings[key] || null
}

/** Persist a mapping. `productCode` of '' or null clears it. */
export function saveProductMapping(dept, rawName, productCode) {
  if (!dept) return
  const key = normaliseMappingKey(rawName)
  if (!key) return
  const mappings = getProductMappings(dept)
  if (productCode) {
    mappings[key] = productCode
  } else {
    delete mappings[key]
  }
  try {
    localStorage.setItem(lsKey(dept), JSON.stringify(mappings))
  } catch {
    /* quota / serialization — ignore */
  }
}

/** Clear all mappings for a department. */
export function clearProductMappings(dept) {
  try { localStorage.removeItem(lsKey(dept)) } catch { /* ignore */ }
}
